/**
 * @file   src\components\AgentWantAd\WantAdDescription.js
 * @brief  This file is responsible for handling wantad description in edit want-ad.
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useRef } from "react";
import { FormLabel } from "react-bootstrap";
import TextBox from "../Common/TextBox";
import TextArea from "../Common/TextArea";
import { handleTextBoxChange } from "../../actions/common";
import { ERROR_TEXT } from "../../constants/common";
import { trimString } from "../../helpers/common";
import { checkFairHousing } from "../../services/v2/wantAdService";

const WantAdDescription = (props) => {
  const {
    handleFormValues,
    adTitle,
    adDescription,
    errors,
    titleTextBoxError,
    descriptionError = false,
    isFromBuyersListingService,
    descriptionMaxLength = 2000,
    fairHousingAcknowledged = false,
    onFairHousingChange,
    onFairHousingViolationsChange,
  } = props;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descError, setDescError] = useState(null);
  const [titleError, setTitleError] = useState(null);
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [descCharCount, setDescCharCount] = useState(0);
  const [titleCursorPos, setTitleCursorPos] = useState(0);
  const [descCursorPos, setDescCursorPos] = useState(0);
  const [fhViolations, setFhViolations] = useState([]);
  const [fhChecking, setFhChecking] = useState(false);

  const titleRef = useRef();
  const descriptionRef = useRef();
  const fhTimerRef = useRef(null);

  useEffect(() => {
    setTitle(adTitle);
    setDescription(adDescription);
    setTitleCharCount(adTitle && adTitle.length ? adTitle.length : 0);
    setDescCharCount(
      adDescription && adDescription.length ? adDescription.length : 0,
    );
  }, [adTitle, adDescription, titleError, descError]);

  // to show error message
  useEffect(() => {
    if (errors && errors.length > 0) {
      if (errors[0] == "adTitle") {
        titleRef.current.focus();
      } else if (errors[0] == "adDesc") {
        descriptionRef.current.focus();
      }

      errors.map((item) => {
        if (item === "adTitle") setTitleError(ERROR_TEXT);
        if (item === "adDesc") setDescError(ERROR_TEXT);
      });
    } else {
      setTitleError(null);
      setDescError(null);
    }
  }, [errors]);

  useEffect(() => {
    if (!isFromBuyersListingService) {
      titleRef.current.setSelectionRange(titleCursorPos, titleCursorPos);
    }
  }, [titleCursorPos]);

  useEffect(() => {
    descriptionRef.current.setSelectionRange(descCursorPos, descCursorPos);
  }, [descCursorPos]);

  // Debounced fair housing check - fires 1s after user stops typing
  useEffect(() => {
    if (fhTimerRef.current) clearTimeout(fhTimerRef.current);
    if (!description || description.trim().length < 10) {
      setFhViolations([]);
      if (onFairHousingViolationsChange) onFairHousingViolationsChange([]);
      return;
    }
    fhTimerRef.current = setTimeout(() => {
      setFhChecking(true);
      checkFairHousing(description)
        .then((res) => {
          const result = res?.result;
          const list = result?.hasViolations ? (result.violations || []) : [];
          setFhViolations(list);
          if (onFairHousingViolationsChange) onFairHousingViolationsChange(list);
        })
        .catch((err) => {
          console.error('[FH Check Error]', err);
          setFhViolations([]);
          if (onFairHousingViolationsChange) onFairHousingViolationsChange([]);
        })
        .finally(() => setFhChecking(false));
    }, 1000);
    return () => { if (fhTimerRef.current) clearTimeout(fhTimerRef.current); };
  }, [description]);

  /**
   * To handle title field changes
   * @param {*} e
   */
  const handleTitleChange = (e) => {
    setTitleError("");
    const value = capitalizeFirstString(handleTextBoxChange(e));
    setTitle(value);
    setTitleCharCount(value.length);
    if (trimString(value) !== "") {
      handleFormValues({ adTitle: value });
      setTitleCursorPos(e.target.selectionStart);
    } else {
      handleFormValues({ adTitle: "" });
    }
  };

  const sentenceCase = (input, lowercaseBefore) => {
    setDescError("");
    input = input === undefined || input === null ? "" : input;
    if (lowercaseBefore) {
      input = input.toLowerCase();
    }
    return input
      .toString()
      .replace(/(^|\. *)([a-z])/g, function (match, separator, char) {
        return separator + char.toUpperCase();
      });
  };

  const capitalizeFirstString = (str) => {
    return str && str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleDescriptionChange = (e) => {
    let value = capitalizeFirstString(handleTextBoxChange(e));
    value = sentenceCase(value);
    setDescCharCount(value.length);
    setDescription(value);
    if (trimString(value) !== "") {
      handleFormValues({ adDesc: value });
      setDescCursorPos(e.target.selectionStart);
    } else {
      handleFormValues({ adDesc: "" });
    }
  };

  return (
    <div
      className={
        titleTextBoxError || descriptionError
          ? "wantad-des-wrap select-type-wrap red-marked-wrap"
          : "wantad-des-wrap select-type-wrap "
      }
    >
      {isFromBuyersListingService ? (
        <></>
      ) : (
        <div className="position-relative wanted-ad-description">
          <FormLabel className={titleTextBoxError && "red-marked-label"}>
            Title <span>*</span>
          </FormLabel>
          <TextBox
            id="title"
            name="title"
            type="text"
            className={titleTextBoxError && "red-marked-formcontrol"}
            errorMessage={titleError}
            value={title ? title : ""}
            maxLength={100}
            placeholder="Fix & flip in Tempe w/ space for a pool & preferably hardwood floors."
            onChange={(e) => {
              handleTitleChange(e);
            }}
            txtRef={titleRef}
            onBlur={() => {
              handleFormValues({ focusOut: true });
            }}
          />
          <div
            className={
              titleTextBoxError
                ? "des-count mb-4 des-count-fix red-marked-count"
                : "des-count mb-4 des-count-fix"
            }
          >
            <span>{titleCharCount}</span>/<span>100</span>
          </div>
        </div>
      )}
      <div className="position-relative wanted-ad-description">
        <FormLabel className={descriptionError && "red-marked-label"}>
          Description
        </FormLabel>
        <TextArea
          className={
            descriptionError
              ? "form-control txt-area red-marked-formcontrol"
              : "form-control txt-area "
          }
          errorMessage={descError}
          value={description ? description : ""}
          maxLength={descriptionMaxLength}
          placeholder="Looking to find a fix a flip opportunity in Tempe. If you have anything, let me know! If it has room for a pool or already has a pool, that would be great. Hardwood floors is a plus but not a must."
          onChange={(e) => {
            handleDescriptionChange(e);
          }}
          txtRef={descriptionRef}
          onBlur={() => {
            handleFormValues({ focusOut: true });
          }}
        />

        <div
          className={
            descriptionError ? "des-count red-marked-count" : "des-count"
          }
        >
          <span>{descCharCount}</span>/<span>{descriptionMaxLength}</span>
        </div>
        {fhViolations.length > 0 && (
          <div style={{
            marginTop: 10,
            padding: "12px 16px",
            borderRadius: 8,
            background: "#FFF4E5",
            border: "1px solid #FFD699",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}>
            <div style={{
              width: 20,
              height: 20,
              flexShrink: 0,
              marginTop: 1,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#BF6A02">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#1C1E21",
                margin: "0 0 6px 0",
                fontFamily: "'Supreme-Bold', sans-serif",
              }}>
                Fair Housing Review
              </p>
              {fhViolations.map((v, i) => (
                <p key={i} style={{
                  fontSize: 13,
                  color: "#1C1E21",
                  margin: "0 0 4px 0",
                  lineHeight: 1.45,
                  fontFamily: "'Supreme-Regular', sans-serif",
                }}>
                  <span style={{
                    fontFamily: "'Supreme-Bold', sans-serif",
                    color: "#1C1E21",
                  }}>
                    &ldquo;{v.text}&rdquo;
                  </span>
                  {" "}&mdash; {v.reason}
                </p>
              ))}
              <p style={{
                fontSize: 12,
                color: "#65676B",
                margin: "8px 0 0 0",
                fontFamily: "'Supreme-Regular', sans-serif",
              }}>
                You may proceed at your discretion. This is not legal advice.
              </p>
            </div>
          </div>
        )}
      </div>
      {onFairHousingChange && (
        <div className="field-wrap mt-3 mb-0">
          <label
            className="form-check-label checkbox-label d-flex align-items-start"
            htmlFor="fairHousingAck"
            style={{ cursor: "pointer" }}
          >
            <input
              className="form-check-input flex-shrink-0"
              type="checkbox"
              id="fairHousingAck"
              checked={fairHousingAcknowledged}
              onChange={(e) => onFairHousingChange(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 13, color: "#374151", marginLeft: 8 }}>
              I acknowledge that this Want-Listing complies with the{" "}
              <a
                href="https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_rights_and_obligations"
                target="_blank"
                rel="noopener noreferrer"
              >
                Federal Fair Housing Act
              </a>
              . Descriptions should focus on property features and should not
              reference buyer demographics.
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
export default WantAdDescription;
